import type { StackFrame, StackFramesSpec } from '../model.js';

export type StackFramePhase = 'entry' | 'call' | 'return';

export function stackFramesFixture(
  width = 640,
  stress = false,
  phase: StackFramePhase = 'call',
  focusFrameId?: string,
): StackFramesSpec {
  const fullFrames = stress ? stressFrames() : normalFrames();
  const frames = framesForPhase(fullFrames, phase);

  return {
    id: `stack-frames-${stress ? 'stress' : 'normal'}-${phase}`,
    family: 'stack-frames',
    view: 'isometric',
    viewport: { width, height: stress ? 570 : 470 },
    growth: 'down',
    frames,
    ...(focusFrameId ? { focusFrameId } : {}),
    provenance: 'Go-style downward call stack teaching fixture · frames, locals, status, and call depth are explicit semantic data',
  };
}

export const defaultStackFocus = 'load-account';
export const stackGuidedTargets = {
  entry: 'frame.serve-http',
  push: 'frame.load-account',
  return: 'frame.load-account',
  resumedCaller: 'frame.serve-http',
} as const;

function framesForPhase(fullFrames: StackFrame[], phase: StackFramePhase): StackFrame[] {
  if (phase === 'entry') {
    const frames = fullFrames.slice(0, Math.max(2, fullFrames.length - 1));
    return frames.map((frame, index) => ({ ...frame, status: index === frames.length - 1 ? 'active' : 'suspended' }));
  }
  if (phase === 'return') {
    return fullFrames.map((frame, index) => ({
      ...frame,
      status: index === fullFrames.length - 2 ? 'active' : index === fullFrames.length - 1 ? 'returned' : 'suspended',
    }));
  }
  return fullFrames.map((frame, index) => ({ ...frame, status: index === fullFrames.length - 1 ? 'active' : 'suspended' }));
}

function normalFrames(): StackFrame[] {
  return [
    {
      id: 'main',
      functionName: 'main.main',
      status: 'suspended',
      locals: [
        { id: 'config', name: 'cfg', value: '*Config', bytes: 8 },
        { id: 'server', name: 'srv', value: '*Server', bytes: 8 },
      ],
    },
    {
      id: 'serve-http',
      functionName: 'api.serveHTTP',
      status: 'suspended',
      locals: [
        { id: 'request', name: 'req', value: '#2471', bytes: 8 },
        { id: 'writer', name: 'w', value: 'Response', bytes: 16 },
        { id: 'account-id', name: 'id', value: 42, bytes: 8 },
      ],
    },
    {
      id: 'load-account',
      functionName: 'store.loadAccount',
      status: 'active',
      locals: [
        { id: 'account-id', name: 'id', value: 42, bytes: 8 },
        { id: 'row', name: 'row', value: '*Row', bytes: 8 },
        { id: 'error', name: 'err', value: 'nil', bytes: 16 },
      ],
    },
  ];
}

function stressFrames(): StackFrame[] {
  return [
    {
      id: 'bootstrap-runtime',
      functionName: 'cmd.bootstrapDistributedRuntime',
      status: 'suspended',
      locals: [
        { id: 'configuration', name: 'configuration', value: '*RuntimeConfiguration', bytes: 8 },
        { id: 'shutdown', name: 'shutdownSignal', value: 'chan struct{}', bytes: 8 },
      ],
    },
    {
      id: 'serve-authenticated-request',
      functionName: 'gateway.serveAuthenticatedCustomerRequest',
      status: 'suspended',
      locals: [
        { id: 'request', name: 'requestContext', value: '#998172', bytes: 8 },
        { id: 'principal', name: 'authenticatedPrincipal', value: 'customer:184467', bytes: 16 },
        { id: 'deadline', name: 'deadline', value: '250ms', bytes: 8 },
        { id: 'trace', name: 'distributedTrace', value: '01J7NKP…', bytes: 16 },
      ],
    },
    {
      id: 'reconcile-inventory',
      functionName: 'inventory.reconcileReservedInventoryUnits',
      status: 'suspended',
      locals: [
        { id: 'warehouse', name: 'warehouseIdentifier', value: 'eu-west-2', bytes: 16 },
        { id: 'reservation', name: 'reservationIdentifier', value: 'res_8fd239', bytes: 16 },
        { id: 'quantity', name: 'requestedQuantity', value: 128, bytes: 8 },
      ],
    },
    {
      id: 'load-snapshot',
      functionName: 'postgres.loadSerializableInventorySnapshot',
      status: 'suspended',
      locals: [
        { id: 'transaction', name: 'transaction', value: '*sql.Tx', bytes: 8 },
        { id: 'statement', name: 'preparedStatement', value: '*sql.Stmt', bytes: 8 },
        { id: 'version', name: 'expectedVersion', value: 1048576, bytes: 8 },
      ],
    },
    {
      id: 'decode-snapshot',
      functionName: 'codec.decodeReplicatedInventorySnapshotEnvelope',
      status: 'active',
      locals: [
        { id: 'payload', name: 'serializedPayload', value: '4096B', bytes: 24 },
        { id: 'destination', name: 'destinationSnapshot', value: '*Snapshot', bytes: 8 },
        { id: 'checksum', name: 'integrityChecksum', value: '7fd4a901', bytes: 8 },
        { id: 'error', name: 'decodeError', value: 'nil', bytes: 16 },
      ],
    },
  ];
}
